import { NARRATION_MAPPING } from './narrationMapping'

export type NarrationLang = 'en' | 'hi'
export type Phrase = string | Partial<Record<NarrationLang, string>>

const STATIC_LOOKUP: Record<NarrationLang, Map<string, string>> = { en: new Map(), hi: new Map() }
for (const entry of NARRATION_MAPPING) {
  STATIC_LOOKUP.en.set(entry.en, entry.slug)
  STATIC_LOOKUP.hi.set(entry.hi, entry.slug)
}
const staticAudioUrl = (text: string, lang: NarrationLang): string | null => {
  const slug = STATIC_LOOKUP[lang].get(text)
  return slug ? `/audio/${lang}/${slug}.wav` : null
}

const STORAGE_KEY = 'kyc_narration_lang'
const LANG_CODE: Record<NarrationLang, string> = { en: 'en-IN', hi: 'hi-IN' }

let currentLang: NarrationLang = 'en'
if (typeof window !== 'undefined') {
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'en' || stored === 'hi') currentLang = stored
}

export const setNarrationLanguage = (lang: NarrationLang) => {
  currentLang = lang
  try {
    window.localStorage.setItem(STORAGE_KEY, lang)
  } catch (error) {
    console.warn('Could not persist narration language', error)
  }
}

export const getNarrationLanguage = (): NarrationLang => currentLang

const resolveText = (phrase: Phrase, lang: NarrationLang) =>
  typeof phrase === 'string' ? phrase : phrase[lang] || phrase.en || phrase.hi || ''

export const t = (phrase: Phrase): string => resolveText(phrase, currentLang)

let audioEl: HTMLAudioElement | null = null
let playToken = 0

const getAudioEl = (): HTMLAudioElement | null => {
  if (typeof window === 'undefined' || typeof Audio === 'undefined') return null
  if (!audioEl) audioEl = new Audio()
  return audioEl
}

const pickVoice = (synth: SpeechSynthesis, lang: NarrationLang) => {
  const voices = synth.getVoices()
  if (!voices.length) return null
  if (lang === 'hi') {
    return (
      voices.find((v) => v.lang === 'hi-IN' && v.name.includes('Google')) ||
      voices.find((v) => v.lang === 'hi-IN') ||
      voices.find((v) => v.lang.startsWith('hi')) ||
      voices[0] ||
      null
    )
  }
  return (
    voices.find((v) => v.name === 'Google US English' || v.voiceURI === 'Google US English') ||
    voices.find((v) => v.name.includes('Google') && v.lang.startsWith('en')) ||
    voices.find((v) => v.lang === 'en-US') ||
    voices.find((v) => v.lang.startsWith('en')) ||
    voices[0] ||
    null
  )
}

const speakViaBrowser = (
  text: string,
  lang: NarrationLang,
  token: number,
  volume: number,
  onEnd?: () => void
) => {
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined
  if (!synth) {
    onEnd?.()
    return
  }
  const utterance = new SpeechSynthesisUtterance(text)
  utterance.volume = volume
  if (onEnd) utterance.onend = () => token === playToken && onEnd()

  const assignAndSpeak = () => {
    if (token !== playToken) return
    const voice = pickVoice(synth, lang)
    if (voice) utterance.voice = voice
    utterance.lang = voice?.lang || LANG_CODE[lang]
    synth.speak(utterance)
  }

  if (synth.getVoices().length) {
    assignAndSpeak()
  } else {
    const handler = () => {
      synth.removeEventListener('voiceschanged', handler)
      assignAndSpeak()
    }
    synth.addEventListener('voiceschanged', handler)
    setTimeout(() => {
      synth.removeEventListener('voiceschanged', handler)
      assignAndSpeak()
    }, 1000)
  }
}

export const cancelNarration = () => {
  playToken++
  try {
    if (typeof window !== 'undefined') window.speechSynthesis?.cancel()
  } catch (error) {
    console.warn('Failed to cancel narration', error)
  }
  if (audioEl) {
    try {
      audioEl.pause()
      audioEl.onended = null
      audioEl.onerror = null
    } catch (error) {
      console.warn('Failed to stop narration audio', error)
    }
  }
}

const playUrl = (url: string, token: number, onEnd?: () => void) => {
  const el = getAudioEl()
  if (!el) throw new Error('audio element unavailable')
  el.src = url
  el.onended = () => token === playToken && onEnd?.()
  return el.play()
}

export const speak = (phrase: Phrase, onEnd?: () => void) => {
  const lang = currentLang
  const text = resolveText(phrase, lang)
  if (!text) {
    onEnd?.()
    return
  }

  cancelNarration()
  const token = playToken

  const staticUrl = staticAudioUrl(text, lang)
  if (staticUrl) {
    Promise.resolve()
      .then(() => playUrl(staticUrl, token, onEnd))
      .catch((error) => {
        if (token !== playToken) return
        console.warn('Static narration clip failed, using browser voice', error)
        speakViaBrowser(text, lang, token, 1, onEnd)
      })
    return
  }
  speakViaBrowser(text, lang, token, 1, onEnd)
}

export const warmUpNarration = () => {
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined
  if (synth) {
    try {
      speakViaBrowser('ready', currentLang, playToken, 0)
    } catch (error) {
      console.warn('Narration warm-up failed', error)
    }
  }
  const el = getAudioEl()
  if (el) {
    try {
      el.src =
        'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA='
      const p = el.play()
      if (p && typeof p.then === 'function') p.then(() => el.pause()).catch(() => {})
    } catch (error) {
      console.warn('Audio unlock failed', error)
    }
  }
}
